import type { Sfx } from '../core/audio';
import { RAMPS, type Attr } from '../core/pixelfx';
import { FX_RAMPS, type BurstStyle, type FxRamp } from '../core/skillfx';
import type { WeaponDef } from '../core/types';

/**
 * 무기의 "겉모습" 표 — 모양(fx) 하나가 무엇으로 보이고 어떻게 들리는지.
 *
 * weapons.json / fusions.json 의 fx 값이 이 표의 열쇠다.
 * 새 무기를 만들 때 여기 있는 fx 중 하나를 고르면 그림과 소리가 같이 따라온다.
 * 표에 없는 fx 를 쓰면 속성(attr)에 맞는 기본 모양·소리로 대신한다.
 */
export interface Look {
  /** 나갈 때 나는 소리. 쉬지 않고 도는 궤도·오라는 없다. */
  cast: Sfx | null;
  /** 맞을 때 나는 소리 */
  hit: Sfx | null;
  /** 맞는 소리 음량 배율 (쉬지 않고 때리는 궤도·오라는 작게) */
  hitGain?: number;
  /** 탄이 터지거나 착지할 때의 연출과 소리 */
  burst?: BurstStyle;
  burstSfx?: Sfx;
  /** 탄이 도는 속도 (rad/s) */
  spin?: number;
  /** 속성 램프 대신 쓸 전용 색 */
  ramp?: FxRamp;
  /** 전류가 튀는 선 모양 */
  chain?: 'arc' | 'static';
  /** 장판 그림이 도는 속도 (rad/s) */
  fieldSpin?: number;
  /** 장판 불투명도 (기본 0.9). 자주 깔려서 겹겹이 쌓이는 장판은 옅게 */
  fieldAlpha?: number;
  /** 장판이 펼쳐질 때 한 번 퍼지는 고리 (크게 가끔 나오는 장판) */
  fieldBurst?: BurstStyle;
  /** 무거운 한 방 — 맞으면 화면이 아주 잠깐 멈춘다 */
  heavy?: boolean;
}

export const LOOKS: Record<string, Look> = {
  // ── 베기 ──
  crescent: { cast: 'sword', hit: 'hitSlash' },                         // 【비급】 복마검
  frostfan: { cast: 'frostBlade', hit: 'hitIce' },                      // 서리 대검
  lash: { cast: 'whip', hit: 'hitFire', ramp: 'flame' },                // 화염 채찍
  windcut: { cast: 'windcut', hit: 'hitWind' },                         // 날바람
  hammer: { cast: 'hammer', hit: 'hitRock', hitGain: 1.4, heavy: true },             // 화강암 망치
  thrust: { cast: 'thrust', hit: 'hitFire', ramp: 'flame' },            // 화염의 랜스
  dash: { cast: 'dash', hit: 'hitShock' },                              // 전격 질주
  greatsword: { cast: 'greatsword', hit: 'hitSlash', heavy: true },                  // 예전 회광반조 (1.12 부터 ashblade — 지금은 안 쓴다)
  whirl: { cast: 'whirl', hit: 'hitWind' },                             // 광풍

  // ── 탄 ──
  ember: { cast: 'flame', hit: 'hitFire', ramp: 'flame', burst: 'ember', burstSfx: 'emberBurst' },  // 플레임 스로우 — 맞으면 작게 붉게 터진다
  shard: { cast: 'iceShot', hit: 'hitIce' },                            // 날카로운 얼음
  dart: { cast: 'zap', hit: 'hitShock' },                               // 테이저 볼트
  lightning: { cast: 'thunder', hit: 'hitShock', burst: 'spark', heavy: true },   // 「뇌우」면 하늘에서 떨어져 터진다                      // 썬더브레이크
  bubble: { cast: 'pop', hit: 'hitWind', burst: 'air', burstSfx: 'boomAir', spin: 5 },   // 압축탄
  chunk: { cast: 'stoneThrow', hit: 'hitRock', spin: 9, burst: 'air' },   // 「파편 비」면 떨어진 자리에 흙먼지               // 암석 파편
  fireball: { cast: 'fireball', hit: 'hitFire', burst: 'fire', burstSfx: 'boomFire', ramp: 'flame' },
  wave: { cast: 'splash', hit: 'hitWater', ramp: 'water' },             // 넘실이는 파도
  jet: { cast: 'jet', hit: 'hitWind' },                                 // 제트기류
  quake: { cast: 'quake', hit: 'hitRock' },                             // 지진 파동
  rune: { cast: 'chime', hit: 'hitMagic', spin: 7 },                    // 캐스팅
  royal: { cast: 'royal', hit: 'hitShock', ramp: 'royal', heavy: true },             // 여제의 명령 — 번개 그림 (assets/fx)

  // ── 사방으로 ──
  puff: { cast: 'gasBurst', hit: 'hitFire', ramp: 'flame', spin: 3 },   // 가스 압착
  gustarc: { cast: 'windBlast', hit: 'hitWind' },                       // 역풍
  leaf: { cast: 'breeze', hit: 'hitWind', hitGain: 0.6, ramp: 'leaf', spin: 9 },   // 산들바람
  blade: { cast: 'steel', hit: 'hitSlash', spin: 16 },                  // 날아가는 강철 낫 (지금은 안 쓴다)
  greatblade: { cast: 'clash', hit: 'hitSlash', heavy: true },          // 기사 — 거대한 검
  palm: { cast: 'palm', hit: 'hitIce', ramp: 'ice' },   // 【비급】 소한공 — 윤솔 시트의 한기 소용돌이가 날아간다
  ashblade: { cast: 'ashSwing', hit: 'hitSlash', heavy: true, ramp: 'flame' },   // 회광반조 — 잿빛대검 (받은 그림) + 바닥 검상

  // ── 궤도 ──
  wisp: { cast: null, hit: 'hitFire', hitGain: 0.6, ramp: 'flame' },   // 파이어 스피릿
  spirit: { cast: null, hit: 'hitMagic', hitGain: 0.8 },                // 다중 소환 유지

  // ── 오라 ──
  obsidian: { cast: null, hit: 'grind', ramp: 'obsidian' },             // 옵시디언
  stomp: { cast: null, hit: 'hitRock', hitGain: 0.7 },                  // 타이탄
  cells: { cast: null, hit: 'hitMagic', hitGain: 0.5 },                 // 세포 역설

  // ── 장판 ──
  snow: { cast: 'frostField', hit: null },                              // 에워싸는 눈바람
  fog: { cast: 'fog', hit: null, ramp: 'fog', fieldAlpha: 0.7 },        // 자욱한 안개
  pillar: { cast: 'pillar', hit: null, ramp: 'stone' },                 // 돌기둥
  snowflake: { cast: 'blizzard', hit: null, fieldSpin: 0.5, fieldAlpha: 0.62, fieldBurst: 'air' },  // 풍화설영 — 가끔 크게, 따라다니는 눈보라
  sigil: { cast: 'sigil', hit: null, ramp: 'gold', fieldSpin: 0.25, fieldAlpha: 0.75 },   // 메자이

  // ── 옮겨붙기 ──
  arc: { cast: 'arc', hit: null, chain: 'arc', burst: 'spark' },        // 전류
  static: { cast: 'static', hit: null, chain: 'static', burst: 'spark', ramp: 'violet' }, // 감전

  // ── 뛰어들기 ──
  pounce: { cast: 'pounce', hit: null, burst: 'claw', burstSfx: 'boom', ramp: 'pink' },  // 사냥 준비
};

/** 표에 없는 fx 를 쓴 무기의 기본 소리 */
function fallbackLook(def: WeaponDef): Look {
  const melee = def.behavior === 'melee';
  switch (def.attr) {
    case 'red': return { cast: melee ? 'sword' : 'flame', hit: 'hitFire' };
    case 'blue': return { cast: 'iceShot', hit: 'hitIce' };
    case 'yellow': return { cast: 'arc', hit: 'hitShock', chain: 'arc' };
    case 'grey': return { cast: 'breeze', hit: 'hitWind' };
    case 'brown': return { cast: 'stoneThrow', hit: 'hitRock' };
    case 'steel': return { cast: 'sword', hit: 'hitSlash' };
    default: return { cast: 'chime', hit: 'hitMagic' };
  }
}

export function lookOf(def: WeaponDef): Look {
  const l = LOOKS[def.fx];
  if (!l) return fallbackLook(def);
  // 궤도·오라가 아닌데 cast 가 null 이면(표를 다른 동작에 재활용한 경우) 속성 기본 소리를 쓴다
  if (l.cast === null && def.behavior !== 'orbit' && def.behavior !== 'aura') {
    return { ...l, cast: fallbackLook(def).cast };
  }
  return l;
}

/** 이 무기를 그릴 색 램프 */
export function rampOf(def: WeaponDef): readonly string[] {
  const l = LOOKS[def.fx];
  if (l?.ramp) return FX_RAMPS[l.ramp];
  return RAMPS[def.attr as Attr] ?? RAMPS.white;
}
